/*
 * The editable fee fields shared by the fee-plan editor and the fallback fee config on Stripe
 * settings (final review C-I3, C-I7). Parsing is STRICT: a typo ("0,5", "½", blank) is an inline
 * error and blocks Save -- never coerced to 0, which would silently save a 0% plan or a $0 max.
 * Produces the exact server shape: integer bps and integer cents (property-service FeeConfig).
 */
export interface FeeConfigValue {
  achBps: number;
  achFlatCents: number;
  cardBps: number;
  cardFlatCents: number;
  minCents: number;
  maxCents: number | null;
}

export interface FeeDraftFields {
  achPct: string;
  achFlat: string;
  cardPct: string;
  cardFlat: string;
  min: string;
  noMax: boolean;
  max: string;
}

export type FeeDraftErrors = Partial<Record<'achPct' | 'achFlat' | 'cardPct' | 'cardFlat' | 'min' | 'max', string>>;

export const BLANK_FEE_FIELDS: FeeDraftFields = {
  achPct: '0.50', achFlat: '0', cardPct: '0.50', cardFlat: '0', min: '0', noMax: true, max: '',
};

// Up to 2 decimals: a percent becomes whole basis points, dollars become whole cents.
const DECIMAL_2 = /^\d+(\.\d{1,2})?$/;

function parsePercentToBps(raw: string): number | string {
  const s = raw.trim();
  if (!DECIMAL_2.test(s)) return 'Enter a percent like 0.5 or 2.9';
  const bps = Math.round(Number(s) * 100);
  return bps > 10_000 ? 'Must be 100% or less' : bps;
}

function parseDollarsToCents(raw: string): number | string {
  const s = raw.trim();
  if (!DECIMAL_2.test(s)) return 'Enter an amount like 0 or 0.30';
  return Math.round(Number(s) * 100);
}

export function parseFeeDraft(d: FeeDraftFields): { config: FeeConfigValue | null; errors: FeeDraftErrors } {
  const errors: FeeDraftErrors = {};
  const take = (field: keyof FeeDraftErrors, v: number | string): number => {
    if (typeof v === 'string') { errors[field] = v; return 0; }
    return v;
  };
  const achBps = take('achPct', parsePercentToBps(d.achPct));
  const achFlatCents = take('achFlat', parseDollarsToCents(d.achFlat));
  const cardBps = take('cardPct', parsePercentToBps(d.cardPct));
  const cardFlatCents = take('cardFlat', parseDollarsToCents(d.cardFlat));
  const minCents = take('min', parseDollarsToCents(d.min));
  let maxCents: number | null = null;
  if (!d.noMax) {
    if (!d.max.trim()) errors.max = 'Enter a maximum, or turn on "No max"';
    else {
      maxCents = take('max', parseDollarsToCents(d.max));
      if (!errors.max && !errors.min && maxCents < minCents) errors.max = 'Max can’t be below Min';
    }
  }
  if (Object.keys(errors).length > 0) return { config: null, errors };
  return { config: { achBps, achFlatCents, cardBps, cardFlatCents, minCents, maxCents }, errors };
}

export function feeConfigToFields(c: FeeConfigValue): FeeDraftFields {
  return {
    achPct: (c.achBps / 100).toFixed(2),
    achFlat: (c.achFlatCents / 100).toFixed(2),
    cardPct: (c.cardBps / 100).toFixed(2),
    cardFlat: (c.cardFlatCents / 100).toFixed(2),
    min: (c.minCents / 100).toFixed(2),
    noMax: c.maxCents === null,
    max: c.maxCents === null ? '' : (c.maxCents / 100).toFixed(2),
  };
}
